/**
 * Serialisierung von Schreibzugriffen JE ENTITAET (AC-016, AK-5).
 *
 * DAS PROBLEM. Zwei Mutationen auf derselben Entitaet in schneller Folge laufen
 * standardmaessig nebenlaeufig. Sie koennen in beliebiger Reihenfolge am Server
 * ankommen, und die zweite Antwort kann vor der ersten eintreffen. Traegt die
 * Oberflaeche dann ein, „was zuletzt zurueckkam", steht am Ende der AELTERE
 * Wert in der Anzeige — und beim naechsten Neuladen ein dritter.
 *
 * DIE GRENZE, ausdruecklich benannt: Das hier serialisiert innerhalb EINES
 * Browser-Tabs. Zwei Personen, oder dieselbe Person in zwei Tabs, laufen
 * weiterhin gegeneinander. Dagegen hilft nur eine Pruefung am Server —
 * `projects.version` liegt dafuer seit AC-014 im Schema und ist bis heute
 * ungenutzt (CLAUDE.md §14). Wer diese Warteschlange fuer die ganze Antwort auf
 * Wettlaeufe haelt, baut auf Sand.
 *
 * WARUM BEIDE AUFRUFE LAUFEN und nicht der erste verworfen wird: Restaufwand
 * ist append-only (ADR-10) — jede Schaetzung ist ein eigener Eintrag mit
 * Zeitpunkt und Urheber. Den ersten von zwei Aufrufen zu verwerfen, weil ein
 * zweiter nachkam, wuerde genau die Historie verschlucken, die ADR-10
 * verlangt. „Der letzte gewinnt" waere hier also nicht schneller, sondern
 * falsch.
 *
 * WARUM NICHT TANSTACK QUERY. Dessen Mutationen kennen eine eingebaute
 * Mechanik dafuer. Sie haengt aber an einem `QueryClient` und an Mutationen,
 * die ueber dessen Haken angemeldet sind — beides setzt den Lesepfad und die
 * fuenf Fachmutationen voraus, und die gibt es hier noch nicht
 * (Entscheidungsprotokoll E1 und E5). Diese Warteschlange kennt weder React
 * noch tRPC und laesst sich spaeter unveraendert dahinter oder daneben legen.
 */

/**
 * Bildet den Schluessel, unter dem serialisiert wird.
 *
 * Als Funktion und nicht als Zeichenkette an der Aufrufstelle: zwei Aufrufer,
 * die `"ap-1"` und `"ap:1"` schreiben, haetten zwei Warteschlangen fuer
 * dieselbe Entitaet — und die Serialisierung waere lautlos weg.
 *
 * MASSGEBLICH IST DIE ENTITAET, DIE DER SERVER SCHREIBT, nicht die Prozedur.
 * Zwei verschiedene Prozeduren, die dasselbe Arbeitspaket aendern, gehoeren
 * unter denselben Schluessel; dieselbe Prozedur auf zwei Arbeitspaketen unter
 * zwei verschiedene.
 */
export function entitaetsSchluessel(typ: string, id: string): string {
  return `${typ}:${id}`;
}

export interface Serialisierer {
  /**
   * Haengt `aufgabe` hinten an die Warteschlange von `schluessel` an.
   *
   * Der zurueckgegebene Versprechenswert ist der der AUFGABE — Erfolg wie
   * Fehler kommen unveraendert beim Aufrufer an. Die Warteschlange verschluckt
   * nichts und verwandelt nichts.
   *
   * NICHT VERSCHACHTELN. Wartet eine Aufgabe von innen heraus auf `reihe()`
   * mit DEMSELBEN Schluessel, stehen beide fuer immer: Die innere wartet auf
   * das Ende der aeusseren, die aeussere auf das Ergebnis der inneren. Es gibt
   * absichtlich keine Zeitschranke, die das aufloest — eine Zeitschranke
   * machte aus einem Programmierfehler einen abgebrochenen Schreibzugriff, und
   * der saehe aus wie ein Netzfehler. Wer in einer Aufgabe eine zweite
   * Mutation braucht, ruft sie DIREKT auf; sie laeuft ohnehin schon unter dem
   * Schutz der Warteschlange. (Befund aus dem KI-Agent-Review.)
   */
  reihe<T>(schluessel: string, aufgabe: () => Promise<T>): Promise<T>;
  /** Wie viele Aufgaben unter diesem Schluessel noch laufen oder warten. */
  offen(schluessel: string): number;
  /** Wie viele Schluessel gerade ueberhaupt beschaeftigt sind. */
  beschaeftigteSchluessel(): number;
}

export function erzeugeSerialisierer(): Serialisierer {
  /**
   * Je Schluessel das Ende der Kette.
   *
   * Das hier abgelegte Versprechen scheitert NIE — es wird gleich unten
   * entschaerft. Waere es das nicht, haette eine gescheiterte Aufgabe zwei
   * Folgen: die naechste Aufgabe liefe nie an, und der Browser meldete eine
   * unbehandelte Ablehnung fuer ein Versprechen, das niemand haelt.
   */
  const schwaenze = new Map<string, Promise<void>>();
  const offeneAufgaben = new Map<string, number>();

  function abschliessen(schluessel: string): void {
    const rest = (offeneAufgaben.get(schluessel) ?? 1) - 1;

    if (rest > 0) {
      offeneAufgaben.set(schluessel, rest);
      return;
    }

    // Leer geraeumt. Ohne dieses Aufraeumen waechst die Map mit jeder je
    // beruehrten Entitaet — in einer Ansicht mit Hunderten Arbeitspaketen ist
    // das ein Leck, das niemand bemerkt, weil nichts kaputtgeht.
    offeneAufgaben.delete(schluessel);
    schwaenze.delete(schluessel);
  }

  return {
    reihe<T>(schluessel: string, aufgabe: () => Promise<T>): Promise<T> {
      const vorgaenger = schwaenze.get(schluessel) ?? Promise.resolve();

      offeneAufgaben.set(schluessel, (offeneAufgaben.get(schluessel) ?? 0) + 1);

      // `vorgaenger` scheitert nie (siehe oben), deshalb genuegt ein `then`:
      // die Aufgabe startet, wenn die vorige FERTIG ist — ob erfolgreich oder
      // nicht. Eine gescheiterte Mutation darf die naechste nicht blockieren;
      // sonst stuende die Entitaet nach einem Netzfehler dauerhaft still.
      const lauf = vorgaenger.then(aufgabe);

      const schwanz: Promise<void> = lauf.then(
        () => {
          abschliessen(schluessel);
        },
        () => {
          abschliessen(schluessel);
        },
      );

      schwaenze.set(schluessel, schwanz);

      return lauf;
    },

    offen(schluessel: string): number {
      return offeneAufgaben.get(schluessel) ?? 0;
    },

    beschaeftigteSchluessel(): number {
      return schwaenze.size;
    },
  };
}

/**
 * DIE Warteschlange der Anwendung. Fachcode benutzt diese und nicht die Fabrik.
 *
 * BEFUND AUS DEM KI-AGENT-REVIEW, und er trifft den Kern von AK-5: Vorher gab
 * es nur die Fabrik. Zwei Aufrufstellen, die sich je eine eigene Warteschlange
 * bauen — und genau so stand es im README-Beispiel —, serialisieren NICHT
 * gegeneinander. Zwei Komponenten, die dasselbe Arbeitspaket aendern, konnten
 * sich weiterhin ueberholen; die Zusage war formal erfuellt und praktisch
 * wirkungslos.
 *
 * Kein Test kann das fangen: Jeder Test baut sich eine eigene Instanz, und
 * genau darin besteht der Fehler. Die Abhilfe ist deshalb strukturell — es
 * gibt eine, und sie steht hier.
 *
 * `erzeugeSerialisierer` bleibt exportiert, aber fuer TESTS: Die sollen sich
 * gerade nicht gegenseitig blockieren.
 */
export const geteilterSerialisierer: Serialisierer = erzeugeSerialisierer();
